const parse = require("color-parse");
const GLOBAL_CONFIGS = require('store/data/global_configs.json');

export const parseColor = (color) => {
  if (!color) {
    return {'hex': null, 'rgb': null}
  }
  const parsedColor = parse(color);
  if (!parsedColor.space) {
    return null
  }
  const rgb = [parsedColor.values[0], parsedColor.values[1], parsedColor.values[2]];
  const hex = rgbToHex(rgb);
  return {'hex': hex, 'rgb': rgb}
}

function _increaseBrightness(color, percent) {
  // strip the leading # if it's there
  let r;
  let g;
  let b;
  try {
    r = color[0];
    g = color[1];
    b = color[2];
  } catch (error) {
    let hex = color.replace(/^\s*#|\s*$/g, '');

    if(hex.length === 3){
        hex = hex.replace(/(.)/g, '$1$1');
    }

    r = parseInt(hex.substr(0, 2), 16);
    g = parseInt(hex.substr(2, 2), 16);
    b = parseInt(hex.substr(4, 2), 16);
  }

  return '#' +((0|(1<<8) + r + (256 - r) * percent / 100).toString(16)).substr(1) +
     ((0|(1<<8) + g + (256 - g) * percent / 100).toString(16)).substr(1) +
     ((0|(1<<8) + b + (256 - b) * percent / 100).toString(16)).substr(1);
}

/** Default feature colors, declared in global_configs. Trips are drawn in the
 *  brand color; locations in a neutral dark. Exported so callers can pick the
 *  right fallback for the kind of feature they're building. */
export const DEFAULT_TRIP_COLOR = GLOBAL_CONFIGS.mapping.colors.trip;
export const DEFAULT_LOCATION_COLOR = GLOBAL_CONFIGS.mapping.colors.location;

/**
 * Processes a color input and returns its RGB and hex representations,
 * along with hover state variants with increased brightness.
 *
 * @param {string} color - The color to process (can be in RGB, hex, or other parseable format)
 * @param {string} defaultColor - Hex color used when `color` is empty. Defaults
 *   to the configured location color; trips pass DEFAULT_TRIP_COLOR.
 * @returns {Object} An object containing:
 *   - rgbColor: RGB representation of the base color
 *   - hexColor: Hex representation of the base color
 *   - rgbColorHover: RGB representation of the hover state color (brightened)
 *   - hexColorHover: Hex representation of the hover state color (brightened)
 */
export function getColors (color, defaultColor = DEFAULT_LOCATION_COLOR) {
  let rgbColor;
  let hexColor;
  const parsedColor = color ? parseColor(color) : null;
  if (parsedColor && parsedColor.rgb) {
    rgbColor = parsedColor.rgb;
    hexColor = parsedColor.hex;

  } else {
    // No color, or an unparseable one. The color column is user-editable and
    // has no real validator, so a typo like "blu" reaches here as an
    // unparseable value; parseColor returns null and reading `.rgb` off it used
    // to throw and crash the whole map render. Fall back to the default color
    // instead.
    // `hexColor` used to be left undefined here, so every uncolored feature
    // published `colorHex: undefined`. Mapbox's ["get","colorHex"] then
    // resolved to null and fell back to its built-in black, and colorInt
    // became -1 — silently defeating the cluster color-ring aggregation.
    hexColor = defaultColor;
    rgbColor = hexToRgb(defaultColor);
  }

  let rgbColorHover;
  let hexColorHover;
  if (rgbColor) {
    const parsedColorHover = parseColor(_increaseBrightness(rgbColor, 50));
    rgbColorHover = parsedColorHover.rgb;
    hexColorHover = parsedColorHover.hex;
  } else {
    hexColorHover = GLOBAL_CONFIGS.mapping.colors['location-hover']
    rgbColorHover = hexToRgb(hexColorHover);
  }

  return {'rgbColor': rgbColor, 'hexColor': hexColor, 'hexColorHover': hexColorHover, 'rgbColorHover': rgbColorHover}
}

const componentToHex = (input) => {
  const hex = input.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
}

function rgbToHex(rgb) {
  return "#" + componentToHex(rgb[0]) + componentToHex(rgb[1]) + componentToHex(rgb[2]);
}

//https://stackoverflow.com/a/5624139
export default function hexToRgb(hex) {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function(m, r, g, b) {
      return r + r + g + g + b + b;
    });
  
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    
    return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
    ] : null;

  }